import { useSelector, useDispatch } from 'react-redux';
import { useEffect } from 'react';
import { selectAuth, selectBubUser, selectUser } from '../selectors/user';
import {
  selectBrandSettings,
  selectConfig,
  selectSelectedCar,
  selectShowBubMigrationModal,
  selectXBrandId,
} from '../selectors';
import { selectFrom, selectBackby } from '../selectors/carsSearch';
import { setShowBubMigrationModal } from '../redux/store/ui/map/Actions';
import {
  generateMagicLink,
  getUser,
  setBubUser,
} from '../redux/store/userData/user/actions';
import { injectedLinking } from '../redux/ConfigureGlobals';
import { isWeb } from '../helpers/isWeb';
import { isAfter } from 'date-fns';
import { dateStringFromDate } from '../helpers/dateFormatters';
import { localizeStandalone } from '../helpers/localizeStandalone';

export const useBubMigration = (
  isUbq: boolean,
  showModalInitially: boolean = false
) => {
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const basicAuth = useSelector(selectAuth);
  const xBrandId = useSelector(selectXBrandId);
  const { bundleId } = useSelector(selectConfig);
  const selectedCar = useSelector(selectSelectedCar);
  const showBubModalFlag = useSelector(selectShowBubMigrationModal);
  const migratedBubUser = useSelector(selectBubUser);
  const from = useSelector(selectFrom) as any;
  const backBy = useSelector(selectBackby) as any;
  const brandSettings = useSelector(selectBrandSettings);

  const ubqShutdownDue =
    brandSettings?.sunsetDate &&
    isAfter(new Date(), new Date(brandSettings?.sunsetDate));

  const isDeUbqShutdown = isUbq && !migratedBubUser && ubqShutdownDue;

  const formattedUbqShutdownDate =
    isUbq && brandSettings?.sunsetDate
      ? dateStringFromDate(
          new Date(brandSettings?.sunsetDate),
          localizeStandalone
        )
      : '';

  const isStaging = isWeb()
    ? process.env.VITE_STAGE !== 'production'
    : bundleId?.includes('staging');
  const bubBrandId = isStaging
    ? '68db99e99c525855188c70a1'
    : '68d2b8cdfc85d1466c5a0a29';

  const title = migratedBubUser ? 'bub.modal.title.at' : 'bub.modal.title';
  const description = migratedBubUser ? 'bub.modal.desc.at' : 'bub.modal.desc';
  const ctaText = migratedBubUser ? 'bub.modal.cta.at' : 'bub.modal.cta';
  const secondaryCTAText = migratedBubUser
    ? 'bub.modal.link.at'
    : 'bub.modal.link';
  const showSecondaryCTA = !!user;

  const onClose = () => {
    dispatch(setShowBubMigrationModal(false));
  };

  const onCTA = () => {
    if (migratedBubUser) {
      dispatch(
        // @ts-ignore
        generateMagicLink(
          false,
          (res) => {
            let magicLink = res.value as string;
            if (magicLink) {
              // Append optional id/from/to parameters
              const extra: string[] = [];
              if (selectedCar?.id)
                extra.push(`id=${encodeURIComponent(selectedCar.id)}`);
              if (from) extra.push(`from=${encodeURIComponent(from)}`);
              if (backBy) extra.push(`backBy=${encodeURIComponent(backBy)}`);

              // Add /map/ before query params for web, handling amplify trailing slash issue
              if (!magicLink.includes('/map')) {
                const [baseUrl, queryParams] = magicLink.split('?');
                magicLink = `${baseUrl}map/${
                  queryParams ? '?' + queryParams : ''
                }`;
              }

              if (extra.length) magicLink = `${magicLink}&${extra.join('&')}`;

              if (!isWeb()) {
                injectedLinking.canOpenURL(magicLink).then((supported: any) => {
                  if (supported) {
                    injectedLinking.openURL(magicLink);
                  }
                });
              } else {
                window.open(magicLink, '_blank');
              }
            }
          },
          bubBrandId
        )
      );
    } else {
      const bubLink = 'https://buchbinder.app';
      if (!isWeb()) {
        injectedLinking.canOpenURL(bubLink).then((supported: any) => {
          if (supported) {
            injectedLinking.openURL(bubLink);
          }
        });
      } else {
        window.open(bubLink, '_blank');
      }
    }
  };

  const onSecondaryCTA = () => {
    onClose();
  };

  // UseEffect
  useEffect(() => {
    if (isUbq && basicAuth && !migratedBubUser) {
      dispatch(
        // @ts-ignore
        getUser(
          (bubUser) => {
            if (bubUser) {
              dispatch(setBubUser(bubUser));
              if (showModalInitially) dispatch(setShowBubMigrationModal(true));
            }
          },
          () => {},
          bubBrandId
        )
      );
    } else if (migratedBubUser && showModalInitially) {
      dispatch(setShowBubMigrationModal(true));
    }
  }, []);

  const onShutdownCTA = async () => {
    const shutdownLink = await localizeStandalone('ubq.shutdown.link');
    if (!isWeb()) {
      injectedLinking.canOpenURL(shutdownLink).then((supported: any) => {
        if (supported) {
          injectedLinking.openURL(shutdownLink);
        }
      });
    } else {
      window.open(shutdownLink, '_blank');
    }
  };

  return {
    user,
    migratedBubUser,
    xBrandId,
    showBubModalFlag,
    title,
    description,
    ctaText,
    secondaryCTAText,
    showSecondaryCTA,
    onClose,
    onCTA,
    onSecondaryCTA,
    isDeUbqShutdown,
    formattedUbqShutdownDate,
    onShutdownCTA,
  };
};
